import { useMutation } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { EXCHANGE } from "../../Services/ExchangeSelectorContextProvider";
import { queryClient } from "../../api/queryClient";
import { ExecutionModeEndpoint, setExecutionMode } from "./lazerApi";
import { useNotification } from "./contexts/notifications";

const NAME = EXCHANGE.toUpperCase();

const MODES: Record<
  ExecutionModeEndpoint,
  { description: string; success: string }
> = {
  emergency_mode: {
    description: "Emergency mode",
    success: `Emergency mode enabled for ${NAME}`,
  },
  normal_mode: {
    description: "Normal mode",
    success: `${NAME} returned to normal mode`,
  },
};

/**
 * Switches the execution mode of JEPX, refreshing the algo query (which also
 * carries the uptimes the navbar reads the mode from) and notifying the user on
 * the way out. Mutations default to no retries, which is what we want for a
 * destructive admin action on a live exchange - do not add one.
 */
const useExecutionMode = (endpoint: ExecutionModeEndpoint) => {
  const msal = useMsal();
  const { addNotification } = useNotification();
  const { description, success } = MODES[endpoint];

  return useMutation({
    mutationFn: () => setExecutionMode(msal, endpoint, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["v1/algos"] });
      addNotification(success, "success");
    },
    onError: (error) => addNotification(error + "", "error"),
  });
};

/** Puts JEPX into emergency mode, stopping every algo on it. */
export const useEmergencyMode = () => useExecutionMode("emergency_mode");

/** Clears emergency mode on JEPX, starting its stopped algos again. */
export const useNormalMode = () => useExecutionMode("normal_mode");
